<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { Button } from '@/lib/components/ui/button';
  import { dislikeWorkout, downloadActivityFitFile, downloadWorkoutFile, likeWorkout, starActivity } from '@/Util/api';
  import { notifyApiError } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';
  import { askSlopeIntervals } from '@/Common/mixedMode';

  export let exercise: Exercise;

  $: hasWorkout = Boolean(exercise?.workout?.path);

  
  let downloads: ('zwo' | 'tcx' | 'erg')[];
  $: downloads = (exercise && hasWorkout) ? [
    'zwo', 'tcx', 'erg',
  ] : [];

  $: downloadWorkoutIcons = downloads?.map(() => 'fa-download'); // 'idle' | 'sending' | 'sent' | 'failed'

  async function downloadWorkout(path: string, name: string, ext: 'zwo' | 'tcx' | 'erg') {
    downloadWorkoutIcons[downloads.indexOf(ext)] = 'fa-spinner-third animate-spin';
    const slopeIntervals = await askSlopeIntervals(ext, { ...exercise, path });
    if (slopeIntervals === null) {
      downloadWorkoutIcons[downloads.indexOf(ext)] = 'fa-download';
      return;
    }
    const result = await downloadWorkoutFile(path, name, ext, slopeIntervals);
    downloadWorkoutIcons[downloads.indexOf(ext)] = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.WORKOUT_DOWNLOAD_FAILED, message: `Could not download the ${ext.toUpperCase()} file for this workout. Please try again in a moment.` });
  }

  let downloadActivityIcon: string = 'fa-download';
  $: exercise, downloadActivityIcon = 'fa-download';

  async function downloadActivity(path: string, name: string) {
    downloadActivityIcon = 'fa-spinner-third animate-spin';
    const result = await downloadActivityFitFile(path, name);
    downloadActivityIcon = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.ACTIVITY_DOWNLOAD_FAILED, message: "Could not download this activity's file. Please try again in a moment." });
  }

  // TODO (hyde): reactivity to these changes
  function like() {
    if (exercise?.exerciseType === 'Workout') {
      likeWorkout(exercise.path);
      exercise = { ...exercise, liked: !exercise.liked, disliked: false };
    }
  }
  function dislike() { 
    if (exercise?.exerciseType === 'Workout') {
      dislikeWorkout(exercise.path);
      exercise = { ...exercise, disliked: !exercise.disliked, liked: false };
    }
  }
  function favourite() {
    if (exercise?.exerciseType === 'Activity') {
      starActivity(exercise.path, exercise.favorite);
      exercise = { ...exercise, favorite: !exercise.favorite };
    }
  }

</script>

<div class='flex flex-row flex-wrap gap-2'>
  <!-- Downloads -->
  {#await Activity.canDownload(exercise) then canDownload}
    {#if canDownload}
      <Button size='sm' variant='secondary' class='border-0' on:click={() => {downloadActivity(exercise.path, exercise.name)}}>
        <i class={`fa-regular ${downloadActivityIcon} text-xs mr-1`} aria-hidden="true"></i>FIT
      </Button>
    {/if}
  {/await}
  {#if hasWorkout}
    {#each downloads as d}
      <Button size='sm' variant='secondary' class='border-0' on:click={() => {downloadWorkout(Activity.getWorkoutPath(exercise), exercise.name, d)}}>
        <i class={`fa-regular ${downloadWorkoutIcons[downloads.indexOf(d)]} text-xs mr-1`} aria-hidden="true"></i>{d.toUpperCase()}
      </Button>
    {/each}
  {/if}

  <!-- Social / Reaction -->
  {#if exercise?.exerciseType === 'Activity'}
    <Button variant="outline" class='h-8 w-8' on:click={favourite}>
      {#if exercise?.favorite}
        <i class="fa-solid fa-star"></i>
      {:else}
        <i class="fa-regular fa-star"></i>
      {/if}
    </Button>
  {:else if exercise?.exerciseType === 'Workout'}
    <Button variant="outline" class='h-8 w-8' on:click={like}>
      <i class={`${exercise?.liked ? 'fa-solid' : 'fa-regular'} fa-thumbs-up`}></i>
    </Button>
    <Button variant="outline" class='h-8 w-8' on:click={dislike}>
      <i class={`${exercise?.disliked ? 'fa-solid' : 'fa-regular'} fa-thumbs-down`}></i>
    </Button>
  {/if}
</div>
